import React, { useState, useEffect } from 'react';
import nav from '../nav';
import './Sidebar.css';

// Group flat nav items into parent + children based on indent flag.
// Each entry: { id, label, badge, children[] }
function groupItems(items) {
  const groups = [];
  items.forEach((item) => {
    if (item.indent) {
      if (groups.length > 0) {
        groups[groups.length - 1].children.push(item);
      }
    } else {
      groups.push({ ...item, children: [] });
    }
  });
  return groups;
}

function NavGroup({ section, active, onNavigate }) {
  const grouped = groupItems(section.items);

  // Pre-compute which parents contain the active page (to auto-expand them).
  const activeParents = new Set(
    grouped
      .filter((g) => g.children.some((c) => c.id === active))
      .map((g) => g.id)
  );

  const [collapsed, setCollapsed] = useState(() => {
    const init = {};
    grouped.forEach((g) => {
      if (g.children.length > 0) {
        init[g.id] = !activeParents.has(g.id); // collapsed unless active child inside
      }
    });
    return init;
  });

  function toggle(id) {
    setCollapsed((prev) => ({ ...prev, [id]: !prev[id] }));
  }

  return (
    <div className="sidebar-group">
      <span className="sidebar-group-label">{section.group}</span>
      {section.note && <span className="sidebar-group-note">{section.note}</span>}
      <ul className="sidebar-list">
        {grouped.map((item) => {
          const hasChildren = item.children.length > 0;
          const isCollapsed = hasChildren && collapsed[item.id];

          return (
            <React.Fragment key={item.id}>
              <li className={hasChildren ? 'sidebar-item-row' : ''}>
                <a
                  href={`/${item.id}`}
                  className={`sidebar-item${active === item.id ? ' sidebar-item--active' : ''}`}
                  onClick={(e) => { e.preventDefault(); onNavigate(item.id); }}
                >
                  {item.label}
                  {item.badge && (
                    <span className={`sidebar-badge sidebar-badge--${item.badge}`}>
                      {item.badge}
                    </span>
                  )}
                </a>
                {hasChildren && (
                  <button
                    className={`sidebar-chevron${isCollapsed ? '' : ' sidebar-chevron--open'}`}
                    onClick={() => toggle(item.id)}
                    aria-label={isCollapsed ? 'Expand' : 'Collapse'}
                  >
                    <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                      <path d="M2.5 3.5L5 6.5L7.5 3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                )}
              </li>

              {hasChildren && !isCollapsed && item.children.map((child) => (
                <li key={child.id}>
                  <a
                    href={`/${child.id}`}
                    className={`sidebar-item sidebar-item--indent${active === child.id ? ' sidebar-item--active' : ''}`}
                    onClick={(e) => { e.preventDefault(); onNavigate(child.id); }}
                  >
                    {child.label}
                    {child.badge && (
                      <span className={`sidebar-badge sidebar-badge--${child.badge}`}>
                        {child.badge}
                      </span>
                    )}
                  </a>
                </li>
              ))}
            </React.Fragment>
          );
        })}
      </ul>
    </div>
  );
}

export default function Sidebar({ active, onNavigate, isOpen, industries, industryId, onIndustryChange }) {
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem('valyx-sidebar-collapsed') === 'true'; }
    catch { return false; }
  });

  useEffect(() => {
    try { localStorage.setItem('valyx-sidebar-collapsed', String(collapsed)); }
    catch { /* ignore */ }
  }, [collapsed]);

  return (
    <aside className={`sidebar${isOpen ? ' sidebar--open' : ''}${collapsed ? ' sidebar--collapsed' : ''}`}>
      <div className="sidebar-scroll">
        {industries && (
          <div className="sidebar-industry">
            <label className="sidebar-industry-label" htmlFor="sidebar-industry-select">Industry</label>
            <select
              id="sidebar-industry-select"
              className="sidebar-industry-select"
              value={industryId}
              onChange={(e) => onIndustryChange(e.target.value)}
            >
              {industries.map((ind) => (
                <option key={ind.id} value={ind.id}>{ind.label}</option>
              ))}
            </select>
          </div>
        )}
        <div className="sidebar-inner">
          {nav.map((section) => (
            <NavGroup
              key={section.group}
              section={section}
              active={active}
              onNavigate={onNavigate}
            />
          ))}
        </div>
      </div>
      <button
        type="button"
        className="sidebar-collapse-toggle"
        onClick={() => setCollapsed((c) => !c)}
        aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        <svg width="11" height="11" viewBox="0 0 11 11" fill="none" aria-hidden="true">
          <path d="M7 2.5L3.5 5.5L7 8.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>
    </aside>
  );
}
